import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
  ChevronDownIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  FilterIcon,
  XMarkIcon,
  PencilSquareIcon, 
  EyeIcon
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import { MdOutlineDiscount } from "react-icons/md";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { FaRegCopy } from "react-icons/fa";
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL;

// Date range options
const dateRangeOptions = ['Today', 'This Week', 'This Month', 'Last Month', 'Custom Range'];
const statusOptions = ['Pending', 'Ongoing', 'Resolved', 'Rejected'];
const remarkOptions = {
  Pending: ['Under review', 'Waiting for response'],
  Ongoing: ['Investigation in progress', 'Waiting for customer response'],
  Resolved: ['Issue resolved', 'Refund processed', 'Service restored'],
  Rejected: ['Invalid claim', 'No response from customer', 'Duplicate complaint']
};

const DisputOrder = ({ children }) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [cardData, setCardData] = useState([]);
  const [access_data, setAccess_data] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateRemark, setUpdateRemark] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Check session & encryption
  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      fetchDisputes();
    }
  }, [access_data?.username]);

  useEffect(() => {
    if (filterDrawerOpen) {
      // disable scrolling
      document.body.style.overflow = "hidden";
    } else {
      // enable scrolling again
      document.body.style.overflow = "auto";
    }

    // cleanup when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [filterDrawerOpen]);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const [filters, setFilters] = useState({
    dateRange: 'Today',
    startDate: new Date(),
    endDate: new Date(),
    status: ''
  });

  const applyFilters = () => {
    fetchDisputes();
    setFilterDrawerOpen(false);
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateRange: 'Today',
      startDate: new Date(),
      endDate: new Date(),
      status: ''
    };
    setFilters(defaultFilters);
    setFilterDrawerOpen(false);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate, endDate = today;

    switch (range) {
      case 'Today':
        startDate = new Date(today);
        break;
      case 'This Week':
        startDate = new Date(today.setDate(today.getDate() - today.getDay()));
        break;
      case 'This Month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'Last Month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        startDate = filters.startDate;
        endDate = filters.endDate;
    }

    setFilters(prev => ({
      ...prev,
      dateRange: range,
      startDate,
      endDate
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  // Calculate paginated data
  const getPaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return disputes.slice(startIndex, endIndex);
  };

  const fetchDisputes = async () => {
    setLoading(true);

    try {
      // Format dates for API
      const formatDate = (date) => {
        return date ? new Date(date).toISOString().split('T')[0] : '';
      };

      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          date_range: filters.dateRange,
          from_date: formatDate(filters.startDate),
          to_date: formatDate(filters.endDate),
          status: filters.status,
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setLoading(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}dispute-list.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        setCardData(result.dashboard || []);
        setDisputes(result.data || []);

        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalItems: result.data?.length || 0,
          totalPages: Math.ceil((result.data?.length || 0) / prev.itemsPerPage)
        }));
      } else {
        toast.error(result.message || "Failed to fetch disputes.");
        setDisputes([]);
        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalItems: 0,
          totalPages: 1
        }));
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("API Error:", error);
      setDisputes([]);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        totalItems: 0,
        totalPages: 1
      }));
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(label + " copied successfully");
  }

  const openUpdateModal = (dispute) => {
    setSelectedDispute(dispute);
    setUpdateStatus(dispute.status);
    setUpdateRemark(dispute.remark || '');
    setIsModalOpen(true);
  };

  const openViewModal = (dispute) => {
    setSelectedDispute(dispute);
    setIsViewModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsViewModalOpen(false);
    setSelectedDispute(null);
    setUpdateStatus('');
    setUpdateRemark('');
  };

  const handleStatusChange = (status) => {
    setUpdateStatus(status);
    setUpdateRemark(''); // Reset remark when status changes
  };

  const handleRemarkChange = (remark) => {
    setUpdateRemark(remark);
  };

  const updateDisputeStatus = async () => {
    if (!updateStatus) {
      toast.error('Please select a status');
      return;
    }

    if (!updateRemark) {
      toast.error('Please enter or select a remark');
      return;
    }

    setIsUpdating(true);

    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          com_id: selectedDispute.com_id,
          status: updateStatus,
          remark: updateRemark
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setIsUpdating(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}update-dispute.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        toast.success("Dispute updated successfully");
        fetchDisputes(); // Refresh the list
        closeModal();
      } else {
        toast.error(result.message || "Failed to update dispute");
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Update Error:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <div className="flex flex-col flex-1">
        <Header />
        <main className="flex-1 overflow-auto">
          {/* Filter Drawer */}
          <AnimatePresence>
            {filterDrawerOpen && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'tween' }}
                className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium">Filter Disputes</h2>
                    <button
                      onClick={() => setFilterDrawerOpen(false)}
                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Date Range */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date Range</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.dateRange}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {dateRangeOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleDateRangeChange(option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                      {filters.dateRange === 'Custom Range' && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <DatePicker
                              selected={filters.startDate}
                              onChange={(date) => handleFilterChange('startDate', date)}
                              selectsStart
                              startDate={filters.startDate}
                              endDate={filters.endDate}
                              popperPlacement="bottom-start"
                              popperClassName="z-50" // ensures above drawer
                              popperProps={{
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    options: {
                                      boundary: "viewport", // keeps inside screen
                                    },
                                  },
                                ],
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                            />
                          </div>
                          <div>
                            <DatePicker
                              selected={filters.endDate}
                              onChange={(date) => handleFilterChange('endDate', date)}
                              selectsEnd
                              startDate={filters.startDate}
                              endDate={filters.endDate}
                              minDate={filters.startDate}
                              popperPlacement="bottom-end"
                              popperClassName="z-50"
                              popperProps={{
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    options: {
                                      boundary: "viewport",
                                    },
                                  },
                                ],
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.status || 'All'}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {statusOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleFilterChange('status', option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={resetFilters}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Reset
                    </button>
                    <button
                      onClick={applyFilters}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Overlay when filter drawer is open */}
          {filterDrawerOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setFilterDrawerOpen(false)}
            />
          )}
        </main>


        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Back
              </button>
              <h2 className="text-xl font-semibold">
                Dispute Order
              </h2>
              <button
                onClick={() => setFilterDrawerOpen(true)}
                className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
              >
                <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            {children || (
              <>
                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  {cardData.map((card, index) => (
                    <div key={index} className={`${card.color} rounded-lg shadow p-6 text-white`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium opacity-80">{card.title}</p>
                          <h3 className="text-2xl font-bold mt-1">₹{card.count}</h3>
                        </div>
                        <div className="p-2 rounded-full bg-white bg-opacity-20">
                          <MdOutlineDiscount className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Disputes Cards */}
                <div className="space-y-4">
                  {loading && (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                      </div>
                  )}

                  {!loading && getPaginatedData().length > 0 ? (
                    <>
                      {getPaginatedData().length > 0 ? (
  <div className="overflow-x-auto rounded-lg shadow">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
      <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs uppercase">
        <tr>
          <th className="px-3 py-2 text-left font-medium">Complaint ID</th>
          <th className="px-3 py-2 text-left font-medium">Order ID</th>
          <th className="px-3 py-2 text-left font-medium">Date & Time</th>
          <th className="px-3 py-2 text-left font-medium">Reason</th>
          <th className="px-3 py-2 text-left font-medium">Message</th>
          <th className="px-3 py-2 text-left font-medium">Remark</th>
          <th className="px-3 py-2 text-center font-medium">Status</th>
          <th className="px-3 py-2 text-center font-medium">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
        {getPaginatedData().map((dispute, i) => (
          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700">
            
            {/* Complaint ID */}
            <td className="px-3 py-2 align-middle">
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-900 dark:text-white">
                  {dispute.com_id}
                </span>
                <button
                  onClick={() => copyText(dispute.com_id, "Complaint ID")}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Copy Complaint ID"
                >
                  <FaRegCopy className="h-4 w-4" />
                </button>
              </div>
            </td>

            {/* Order ID */}
            <td className="px-3 py-2 align-middle">
              <div className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
                {dispute.order_id}
                <button
                  onClick={() => copyText(dispute.order_id, "Order ID")}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Copy Order ID"
                >
                  <FaRegCopy className="h-4 w-4" />
                </button>
              </div>
            </td>

            {/* Date & Time */}
            <td className="px-3 py-2 align-middle">
              <div className="flex flex-col">
                <span className="text-gray-800 dark:text-gray-200">{dispute.date}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{dispute.createAt}</span>
              </div>
            </td>

            {/* Reason */}
            <td className="px-3 py-2 align-middle text-gray-800 dark:text-gray-200">
              {dispute.reason}
            </td>

            {/* Message */}
            <td className="px-3 py-2 align-middle text-gray-800 dark:text-gray-200 max-w-[200px] truncate">
              {dispute.message}
            </td>

            {/* Remark */}
            <td className="px-3 py-2 align-middle text-gray-800 dark:text-gray-200 max-w-[150px] truncate">
              {dispute.remark || "-"}
            </td>

            {/* Status */}
            <td className="px-3 py-2 align-middle text-center">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full 
                  ${dispute.status === "Resolved"
                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                    : dispute.status === "Rejected"
                    ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    : dispute.status === "Pending"
                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                    : dispute.status === "Ongoing"
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                {dispute.status}
              </span>
            </td>

            {/* Actions */}
            <td className="px-3 py-2 align-middle text-center">
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => openUpdateModal(dispute)}
                  className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                  title="Update Dispute"
                >
                  <PencilSquareIcon className="h-5 w-5 text-blue-500" />
                </button>
                <button
                  onClick={() => openViewModal(dispute)}
                  className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                  title="View Details"
                >
                  <EyeIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
) : (
  <div className="p-4 text-center text-sm text-gray-500">No disputes found</div>
)}

                      {/* Pagination */}
                      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex-1 flex justify-between sm:hidden">
                          <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                          >
                            Next
                          </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
                              <span className="font-medium">
                                {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                              </span>{' '}
                              of <span className="font-medium">{pagination.totalItems}</span> results
                            </p>
                          </div>
                          <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                              <button
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                              >
                                <span className="sr-only">Previous</span>
                                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                              </button>

                              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                let pageNum;
                                if (pagination.totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (pagination.currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                  pageNum = pagination.totalPages - 4 + i;
                                } else {
                                  pageNum = pagination.currentPage - 2 + i;
                                }

                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.currentPage === pageNum
                                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-gray-600 dark:text-white'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                                      }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}

                              <button
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === pagination.totalPages}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                              >
                                <span className="sr-only">Next</span>
                                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                              </button>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    !loading && (
                      <div className="text-center py-12">
                        <p className="text-gray-500 dark:text-gray-400">No disputes found matching your criteria</p>
                      </div>
                    )
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Update Status Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-60 transition-opacity"
              onClick={closeModal}
            ></div>

            {/* Modal content wrapper */}
            <div className="flex items-center justify-center min-h-screen px-4 py-8 text-center">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl z-50"
                role="dialog"
                aria-modal="true"
              >
                {/* Modal Body */}
                <div className="px-6 py-5">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Update Dispute Status
                  </h3>

                  {/* Status Dropdown */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-start">
                      Status
                    </label>
                    <Menu as="div" className="relative">
                      <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-800 dark:text-white">
                        <span>{updateStatus || "Select status"}</span>
                        <ChevronDownIcon className="h-4 w-4" />
                      </Menu.Button>
                      <Menu.Items className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5">
                        {statusOptions.map((option) => (
                          <Menu.Item key={option}>
                            {({ active }) => (
                              <button
                                onClick={() => handleStatusChange(option)}
                                className={`${active
                                  ? "bg-gray-100 dark:bg-gray-600"
                                  : ""
                                  } block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200`}
                              >
                                {option}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Menu>
                  </div>

                  {/* Remark Dropdown + Textarea */}
                  {updateStatus && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-start">
                        Remark
                      </label>
                      <Menu as="div" className="relative mb-2">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-800 dark:text-white">
                          <span>{updateRemark || "Select remark"}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5">
                          {(remarkOptions[updateStatus] || []).map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleRemarkChange(option)}
                                  className={`${active
                                    ? "bg-gray-100 dark:bg-gray-600"
                                    : ""
                                    } block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>

                      {/* Custom Remark Input */}
                      <textarea
                        value={updateRemark}
                        onChange={(e) => setUpdateRemark(e.target.value)}
                        placeholder="Or enter custom remark"
                        rows="3"
                        className="w-full px-3 py-2 mt-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                      />
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 px-4 py-2 bg-white dark:bg-gray-600 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={updateDisputeStatus}
                    disabled={isUpdating}
                    className="inline-flex justify-center rounded-md px-4 py-2 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isUpdating ? "Updating..." : "Update"}
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Details Modal */}
      <AnimatePresence>
        {isViewModalOpen && selectedDispute && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-60 transition-opacity"
              onClick={closeModal}
            ></div>

            {/* Modal content wrapper */}
            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-xl z-50"
                role="dialog"
                aria-modal="true"
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Dispute Details
                  </h3>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Complaint ID</p>
                        <div className="flex items-center mt-1">
                          <p className="text-gray-900 dark:text-white">{selectedDispute.com_id}</p>
                          <button
                            onClick={() => copyText(selectedDispute.com_id, "Complaint ID")}
                            className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Copy Complaint ID"
                          >
                            <FaRegCopy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Order ID</p>
                        <div className="flex items-center mt-1">
                          <p className="text-gray-900 dark:text-white">{selectedDispute.order_id}</p>
                          <button
                            onClick={() => copyText(selectedDispute.order_id, "Order ID")}
                            className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Copy Order ID"
                          >
                            <FaRegCopy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Date</p>
                        <p className="text-gray-900 dark:text-white mt-1">{selectedDispute.date}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Time</p>
                        <p className="text-gray-900 dark:text-white mt-1">{selectedDispute.createAt}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Reason</p>
                        <p className="text-gray-900 dark:text-white mt-1">{selectedDispute.reason}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                        <span
                          className={`mt-1 px-2 py-0.5 text-xs font-medium rounded-full 
                            ${selectedDispute.status === "Resolved"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : selectedDispute.status === "Rejected"
                              ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                              : selectedDispute.status === "Pending"
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                              : selectedDispute.status === "Ongoing"
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {selectedDispute.status}
                        </span>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Remark</p>
                        <p className="text-gray-900 dark:text-white mt-1">{selectedDispute.remark || "No remark provided"}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Message</p>
                    <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-gray-900 dark:text-white">{selectedDispute.message}</p>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="inline-flex justify-center rounded-md px-4 py-2 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer />
    </div>
  );
};

export default DisputOrder;